import httpCommon from "./http-common";
import { AxiosProgressEvent } from "axios";

// Get all inventory categories
const getAllInventoryCategories = (businessId: number) => {
  return httpCommon.get(`/v1/get/inventory/categories/${businessId}`);
};

// Get inventory category by ID
const getInventoryCategory = (categoryId: string) => {
  return httpCommon.get(`/v1/get/inventory/category/${categoryId}`);
};

// Create inventory category
const createInventoryCategory = (businessId: number, data: any) => {
  return httpCommon.post(`/v1/add/inventory/category/${businessId}`, data);
};

// Update inventory category
const updateInventoryCategory = (categoryId: string, data: any) => {
  return httpCommon.put(`/v1/update/inventory/category/${categoryId}`, data);
};

// Delete inventory category
const deleteInventoryCategory = (categoryId: string) => {
  return httpCommon.delete(`/v1/delete/inventory/category/${categoryId}`);
};

// Get all inventory items
const getAllInventoryItems = (businessId: number, skip = 0, limit = 0) => {
  return httpCommon.get(`/v1/get/inventory/items/${businessId}?skip=${skip}&limit=${limit}`);
};

// Get inventory item by ID
const getInventoryItem = (itemId: string) => {
  return httpCommon.get(`/v1/get/inventory/item/${itemId}`);
};

// Create inventory item
const createInventoryItem = (businessId: number, data: any, onUploadProgress?: (progressEvent: AxiosProgressEvent) => void) => {
  return httpCommon.post(`/v1/add/inventory/item/${businessId}`, data, {
    onUploadProgress: onUploadProgress
  });
};

// Update inventory item
const updateInventoryItem = (itemId: string, data: any, onUploadProgress?: (progressEvent: AxiosProgressEvent) => void) => {
  return httpCommon.put(`/v1/update/inventory/item/${itemId}`, data, {
    onUploadProgress: onUploadProgress
  });
};

// Delete inventory item
const deleteInventoryItem = (itemId: string) => {
  return httpCommon.delete(`/v1/delete/inventory/item/${itemId}`);
};

// Search inventory items
const searchInventoryItems = (businessId: number, searchTerm: string) => {
  return httpCommon.get(`/v1/search/inventory/items/${businessId}/${searchTerm}`);
};

// Get inventory item by SKU or barcode
const getInventoryItemBySku = (businessId: number, sku: string) => {
  return httpCommon.get(`/v1/get/inventory/item-by-sku/${businessId}/${sku}`);
};

const getInventoryItemByBarcode = (businessId: number, barcode: string) => {
  return httpCommon.get(`/v1/get/inventory/item-by-barcode/${businessId}/${barcode}`);
};

// Get inventory stock levels
const getAllInventoryStock = (businessId: number, warehouseId?: string) => {
  const warehouseParam = warehouseId ? `?warehouseId=${warehouseId}` : '';
  return httpCommon.get(`/v1/get/inventory/stock/${businessId}${warehouseParam}`);
};

// Get stock by item
const getInventoryStockByItem = (itemId: string, warehouseId?: string) => {
  const warehouseParam = warehouseId ? `?warehouseId=${warehouseId}` : '';
  return httpCommon.get(`/v1/get/inventory/stock/item/${itemId}${warehouseParam}`);
};

// Update stock levels
const updateInventoryStock = (itemId: string, warehouseId: string, data: any) => {
  return httpCommon.put(`/v1/update/inventory/stock/${itemId}/${warehouseId}`, data);
};

// Record inventory movement
const createInventoryMovement = (businessId: number, data: any) => {
  return httpCommon.post(`/v1/add/inventory/movement/${businessId}`, data);
};

// Get inventory movements
const getAllInventoryMovements = (businessId: number, itemId?: string, warehouseId?: string, skip = 0, limit = 0) => {
  let queryParams = `?skip=${skip}&limit=${limit}`;
  if (itemId) queryParams += `&itemId=${itemId}`;
  if (warehouseId) queryParams += `&warehouseId=${warehouseId}`;
  return httpCommon.get(`/v1/get/inventory/movements/${businessId}${queryParams}`);
};

// Get inventory movement by ID
const getInventoryMovement = (movementId: string) => {
  return httpCommon.get(`/v1/get/inventory/movement/${movementId}`);
};

// Get reorder alerts
const getReorderAlerts = (businessId: number) => {
  return httpCommon.get(`/v1/get/inventory/reorder-alerts/${businessId}`);
};

// Update reorder levels
const updateReorderLevel = (itemId: string, warehouseId: string, data: any) => {
  return httpCommon.put(`/v1/update/inventory/reorder-level/${itemId}/${warehouseId}`, data);
};

// Get inventory valuation
const getInventoryValuation = (businessId: number, warehouseId?: string, method = 'FIFO') => {
  const warehouseParam = warehouseId ? `&warehouseId=${warehouseId}` : '';
  return httpCommon.get(`/v1/get/inventory/valuation/${businessId}?method=${method}${warehouseParam}`);
};

// Get inventory aging report
const getInventoryAging = (businessId: number, warehouseId?: string) => {
  const warehouseParam = warehouseId ? `?warehouseId=${warehouseId}` : '';
  return httpCommon.get(`/v1/get/inventory/aging/${businessId}${warehouseParam}`);
};

// Bulk import inventory items
const bulkImportInventoryItems = (businessId: number, data: any, onUploadProgress?: (progressEvent: AxiosProgressEvent) => void) => {
  return httpCommon.post(`/v1/bulk/import/inventory/items/${businessId}`, data, {
    onUploadProgress: onUploadProgress
  });
};

// Export inventory data
const exportInventoryData = (businessId: number, format = 'excel', warehouseId?: string) => {
  const warehouseParam = warehouseId ? `&warehouseId=${warehouseId}` : '';
  return httpCommon.get(`/v1/export/inventory/${businessId}?format=${format}${warehouseParam}`, {
    responseType: 'blob'
  });
};

const InventoryService = {
  // Categories
  getAllInventoryCategories,
  getInventoryCategory,
  createInventoryCategory,
  updateInventoryCategory,
  deleteInventoryCategory,

  // Items
  getAllInventoryItems,
  getInventoryItem,
  createInventoryItem,
  updateInventoryItem,
  deleteInventoryItem,
  searchInventoryItems,
  getInventoryItemBySku,
  getInventoryItemByBarcode,

  // Stock Management
  getAllInventoryStock,
  getInventoryStockByItem,
  updateInventoryStock,

  // Movements
  createInventoryMovement,
  getAllInventoryMovements,
  getInventoryMovement,

  // Reorder Management
  getReorderAlerts,
  updateReorderLevel,

  // Reports
  getInventoryValuation,
  getInventoryAging,

  // Bulk Operations
  bulkImportInventoryItems,
  exportInventoryData
};

export default InventoryService; 